import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, MenuItem, TextField } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { DialogType } from '../../../@types';
import type { CreateGoldGamesCampaign } from '../../../@types/goldGames';
import { DATE_TIME_FORMAT, QUERY_KEYS } from '../../../constants';
import { useToastErrorMessage } from '../../../hooks/useToastErrorMessage';
import { useAwardGoldGames, useGoldGamesBrands, useGoldGamesGames } from '../../../queries/goldGames';
import { goldGamesFreeSpinsSchema, type GoldGamesFreeSpinsFormData } from '../../../schema/goldGamesFreeSpinsSchema';
import FormModalLayout from '../../layouts/FormModalLayout';
import CustomerSelect from '../../molecules/CustomerSelect';

type Props = DialogType & { marketplaceId: string; currency: string };
const defaultValues: GoldGamesFreeSpinsFormData = {
  name: '',
  brandId: '',
  gameId: '',
  quantity: 0,
  betAmount: 0,
  betLevel: 0,
  coinValue: 0,
  playerIds: [],
  startsAt: '',
  endsAt: '',
};

const GoldGamesFreeSpinsForm = ({ marketplaceId, currency, closeModal }: Props) => {
  const form = useForm<GoldGamesFreeSpinsFormData>({
    defaultValues: {
      ...defaultValues,
      startsAt: dayjs().add(2, 'minute').format('YYYY-MM-DDTHH:mm'),
      endsAt: dayjs().add(7, 'day').format('YYYY-MM-DDTHH:mm'),
    },
    resolver: zodResolver(goldGamesFreeSpinsSchema),
    mode: 'onChange',
  });
  const [brandId, gameId, quantity, betAmount] = useWatch({
    control: form.control,
    name: ['brandId', 'gameId', 'quantity', 'betAmount'],
  });
  const brands = useGoldGamesBrands(marketplaceId);
  const games = useGoldGamesGames(marketplaceId, brandId, currency);
  const game = games.data?.games.find((item) => String(item.id) === gameId);
  const award = useAwardGoldGames();
  const { handleErrorMessage } = useToastErrorMessage();
  const locked = award.isPending || award.isSuccess || award.isError;

  const clearBetSettings = () => {
    form.setValue('quantity', 0, { shouldDirty: true, shouldValidate: true });
    form.setValue('betAmount', 0, { shouldDirty: true, shouldValidate: true });
    form.setValue('betLevel', 0, { shouldDirty: true, shouldValidate: true });
    form.setValue('coinValue', 0, { shouldDirty: true, shouldValidate: true });
  };

  const selectProvider = (value: string) => {
    form.setValue('brandId', value, { shouldDirty: true, shouldValidate: true });
    form.setValue('gameId', '', { shouldDirty: true, shouldValidate: true });
    clearBetSettings();
  };

  const selectGame = (value: string) => {
    form.setValue('gameId', value, { shouldDirty: true, shouldValidate: true });
    clearBetSettings();
  };

  const submit = (data: GoldGamesFreeSpinsFormData) => {
    if (!game) return;

    const payload: CreateGoldGamesCampaign = {
      name: data.name,
      brandId: Number(data.brandId),
      gameId: game.id,
      currency,
      betAmount: data.betAmount,
      betLevel: data.betLevel,
      coinValue: data.coinValue,
      numberOfSpins: data.quantity,
      includedUserIds: data.playerIds,
      campaignActivationDate: new Date(data.startsAt).toISOString(),
      campaignExpirationDate: new Date(data.endsAt).toISOString(),
    };
    award.mutate(payload, { onError: handleErrorMessage });
  };

  return (
    <FormModalLayout
      label='Free Spins Campaigns — Gold Games'
      onClose={award.isPending ? undefined : closeModal}
      onSave={form.handleSubmit(submit)}
      actionButtonLabel='Add'
      showEditTabs={false}
      disableSubmit={
        locked ||
        !form.formState.isValid ||
        !game ||
        games.isFetching ||
        brands.isFetching ||
        games.isError ||
        brands.isError
      }
    >
      <Alert severity='info'>
        Enter the quantity and betting settings manually. Gold Games validates the bet level and coin value; acceptance
        of combinations outside its presets is not guaranteed. Dates use your local time and are sent in UTC. Campaigns
        cannot overlap for the same player, game and currency.
      </Alert>
      <TextField
        label='Campaign name'
        required
        {...form.register('name')}
        disabled={locked}
        error={!!form.formState.errors.name}
        helperText={form.formState.errors.name?.message}
        slotProps={{ htmlInput: { maxLength: 120 } }}
      />
      {(brands.isError || games.isError) && (
        <Alert
          severity='error'
          action={
            <Button
              onClick={() => {
                void brands.refetch();
                if (brandId) void games.refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Unable to load Gold Games providers or games.
        </Alert>
      )}
      {brands.isSuccess && !brands.data.brands.length && (
        <Alert severity='warning'>No Gold Games providers support free spins.</Alert>
      )}
      {games.isSuccess && !games.data.games.length && (
        <Alert severity='warning'>No free-spin games are available for this provider and currency.</Alert>
      )}
      <TextField
        select
        label='Provider'
        required
        value={brandId}
        disabled={locked || brands.isFetching || brands.isError}
        onChange={(event) => selectProvider(event.target.value)}
      >
        {(brands.data?.brands ?? []).map((brand) => (
          <MenuItem key={brand.id} value={String(brand.id)}>
            {brand.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label='Game'
        required
        value={gameId}
        disabled={locked || !brandId || games.isFetching || games.isError}
        onChange={(event) => selectGame(event.target.value)}
      >
        {(games.data?.games ?? []).map((item) => (
          <MenuItem key={item.id} value={String(item.id)}>
            {item.name}
          </MenuItem>
        ))}
      </TextField>
      <Controller
        name='quantity'
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={field.value || ''}
            label='Free Spin Quantity'
            type='number'
            required
            disabled={locked || !game}
            onChange={(event) => field.onChange(Number(event.target.value))}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{ htmlInput: { min: 1, step: 1 } }}
          />
        )}
      />
      <Controller
        name='betAmount'
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={field.value || ''}
            label='Free Spin Bet Amount'
            type='number'
            required
            disabled={locked || !game}
            onChange={(event) => field.onChange(Number(event.target.value))}
            error={!!fieldState.error}
            helperText={
              fieldState.error?.message ??
              'Recorded for campaign history. The provider uses bet level and coin value to determine the actual bet.'
            }
            slotProps={{ htmlInput: { min: 0, step: 'any', inputMode: 'decimal' } }}
          />
        )}
      />
      <Controller
        name='betLevel'
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label='Bet Level'
            type='number'
            required
            disabled={locked || !game}
            onChange={(event) => field.onChange(event.target.value === '' ? NaN : Number(event.target.value))}
            value={Number.isNaN(field.value) ? '' : field.value}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
          />
        )}
      />
      <Controller
        name='coinValue'
        control={form.control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            value={field.value || ''}
            label='Coin Value'
            type='number'
            required
            disabled={locked || !game}
            onChange={(event) => field.onChange(Number(event.target.value))}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{ htmlInput: { min: 0, step: 'any', inputMode: 'decimal' } }}
          />
        )}
      />
      {quantity > 0 && betAmount > 0 && (
        <Alert severity='info'>
          Configured total per player: {Number((quantity * betAmount).toPrecision(12))} {currency}
        </Alert>
      )}
      <CustomerSelect
        control={form.control}
        name='playerIds'
        label='Players'
        required
        disabled={locked}
        queryKey={[QUERY_KEYS.customers, 'gold-games-active-players', marketplaceId]}
        queryParams={{ filters: { isActive: true } }}
        error={form.formState.errors.playerIds}
        remoteErrorText='Unable to load active players.'
      />
      {(['startsAt', 'endsAt'] as const).map((name) => (
        <Controller
          key={name}
          name={name}
          control={form.control}
          render={({ field, fieldState }) => (
            <DateTimePicker
              label={name === 'startsAt' ? 'Start (local time)' : 'End (local time)'}
              value={field.value ? dayjs(field.value) : null}
              onChange={(value) => field.onChange(value ? value.format('YYYY-MM-DDTHH:mm') : '')}
              disabled={locked}
              ampm={false}
              format={DATE_TIME_FORMAT}
              slotProps={{
                textField: {
                  required: true,
                  onBlur: field.onBlur,
                  error: !!fieldState.error,
                  helperText: fieldState.error?.message,
                },
              }}
            />
          )}
        />
      ))}
      {award.isPending && (
        <Alert severity='info'>Creating the campaign and registering players. Please keep this window open.</Alert>
      )}
      {award.isError && (
        <Alert severity='error'>
          The campaign request failed. Check the campaign list before trying again: Gold Games may have accepted the
          request. Do not create a duplicate campaign.
        </Alert>
      )}
      {award.data && (
        <Alert severity={award.data.failedPlayerIds.length ? 'warning' : 'success'}>
          Campaign {award.data.campaignId}: awarded to {award.data.awarded} players.
          {award.data.failedPlayerIds.length > 0 &&
            ` ${award.data.failedPlayerIds.length} players could not be confirmed. Close this dialog and use “Retry award” on their existing rows.`}
        </Alert>
      )}
    </FormModalLayout>
  );
};
export default GoldGamesFreeSpinsForm;
